<template>
	<b-modal ref="modal" size="lg" hide-footer @hidden="reset">
		<template v-slot:modal-title>
			{{ $t('tiers.address.trace_import_titre') }}
		</template>

		<!-- Formulaire de recherche -->
		<div class="mb-3">
			<div class="form-group row">
				<label class="col-sm-4 col-form-label">{{ $t('tiers.address.trace_pays') }} *</label>
				<div class="col-sm-8">
					<CountryInput v-model="country_id" />
				</div>
			</div>

			<div class="form-group row">
				<label class="col-sm-4 col-form-label">{{ $t('tiers.address.trace_type') }} *</label>
				<div class="col-sm-8">
					<e-select
						v-model="selected_type"
						:options="type_options"
						label="text"
						track-by="value"
						:placeholder="$t('tiers.address.trace_type_placeholder')"
						:allow-empty="false"
						:show-labels="false"
					/>
				</div>
			</div>

			<div class="form-group row">
				<label class="col-sm-4 col-form-label">{{ $t('tiers.address.trace_mot_cle') }}</label>
				<div class="col-sm-8">
					<b-form-input
						v-model="keyword"
						:placeholder="$t('tiers.address.trace_mot_cle_placeholder')"
						@keydown.enter.prevent="searchTraces"
					/>
				</div>
			</div>

			<div class="text-right">
				<b-button
					variant="primary"
					rounded-pill
					:disabled="loading || !country_id || !selected_type"
					@click="searchTraces"
				>
					<font-awesome-icon v-if="loading" :icon="['fas', 'spinner']" pulse class="mr-1" />
					{{ $t('tiers.address.trace_rechercher') }}
				</b-button>
			</div>
		</div>

		<hr v-if="searched" />

		<!-- Résultats -->
		<div v-if="searched">
			<b-alert v-if="results_truncated" show variant="warning" class="py-2">
				{{ $t('tiers.address.trace_resultats_tronques', { max: 200 }) }}
			</b-alert>

			<p v-if="!loading && filtered_results.length === 0" class="text-muted text-center py-3">
				{{ $t('global.list_empty') }}
			</p>

			<div v-else class="list-group">
				<button
					v-for="item in filtered_results"
					:key="item.operatorId"
					type="button"
					class="list-group-item list-group-item-action"
					:disabled="importing === item.operatorId"
					@click="importAddress(item)"
				>
					<div class="d-flex justify-content-between align-items-center">
						<div>
							<strong>{{ item.operatorName }}</strong>
							<span class="ml-2 badge badge-secondary">{{ item.approvalNumber }}</span>
						</div>
						<font-awesome-icon v-if="importing === item.operatorId" :icon="['fas', 'spinner']" pulse />
					</div>
					<div class="small text-muted mt-1">
						{{ formatAddress(item) }}
					</div>
				</button>
			</div>
		</div>
	</b-modal>
</template>

<script type="text/javascript">
	import Vue from 'vue'
	import Tiers from "@/mixins/Tiers.js"
	import Countries from '@/assets/lang/countries/countries'

	const TRACE_TYPES = [
		{ value: 'SEMEN_COLLECTION_CENTRES',               apiCode: 'SEM-COL', label_key: 'tiers.address.trace_type_sem_col' },
		{ value: 'GERMINAL_PRODUCT_STORAGE_CENTRES',       apiCode: 'SEM-COL', label_key: 'tiers.address.trace_type_germ_sto_sem' },
		{ value: 'EMBRYO_COLLECTION_TEAMS',                apiCode: 'SEM-COL', label_key: 'tiers.address.trace_type_emb_col' },
		{ value: 'EMBRYO_PRODUCTION_TEAMS',                apiCode: 'SEM-COL', label_key: 'tiers.address.trace_type_emb_prod' },
		{ value: 'GERMINAL_PRODUCT_PROCESSING_ESTABLISHMENTS', apiCode: 'GERM-STO', label_key: 'tiers.address.trace_type_germ_sto' }
	]

	const MAX_RESULTS = 200

	export default {
		name: "AddressTraceImport",
		mixins: [Tiers],
		props: {
			tiers_id: {
				type: [Number, String],
				required: true
			}
		},
		data() {
			return {
				country_id: 75,
				selected_type: null,
				keyword: '',
				results: [],
				loading: false,
				searched: false,
				importing: null,
				results_truncated: false
			}
		},
		computed: {
			type_options() {
				return TRACE_TYPES.map(t => ({
					value: t.value,
					text: this.$t(t.label_key)
				}))
			},
			filtered_results() {
				if (!this.keyword || !this.keyword.trim()) {
					return this.results
				}
				const kw = this.keyword.trim().toLowerCase()
				return this.results.filter(item => {
					const cityRef = item.address && item.address.cityReference
					const street = item.address && item.address.street && item.address.street.value
					return (
						(item.operatorName && item.operatorName.toLowerCase().includes(kw)) ||
						(item.approvalNumber && item.approvalNumber.toLowerCase().includes(kw)) ||
						(street && street.toLowerCase().includes(kw)) ||
						(cityRef && cityRef.name && cityRef.name.toLowerCase().includes(kw)) ||
						(cityRef && cityRef.postalCode && cityRef.postalCode.toLowerCase().includes(kw))
					)
				})
			}
		},
		methods: {
			/**
			 * Ouvre la modal d'import TRACES.
			 */
			open() {
				this.$refs.modal.show()
			},
			reset() {
				this.country_id = 75
				this.selected_type = null
				this.keyword = ''
				this.results = []
				this.loading = false
				this.searched = false
				this.importing = null
				this.results_truncated = false
			},
			/**
			 * Retourne le code ISO 3166-1 alpha-2 d'un pays à partir de son ID interne.
			 *
			 * @param {number} country_id ID interne du pays (ex. 75 pour la France).
			 * @returns {string|null} Code ISO2 (ex. 'FR') ou null si introuvable.
			 */
			getIso2(country_id) {
				const locale = Vue.i18n.locale()
				const iso_map = Countries[locale] ? Countries[locale].iso : Countries['fr'].iso
				return iso_map ? iso_map[country_id] : null
			},
			/**
			 * Lance la recherche sur l'API TRACES NT via le proxy Laravel (évite les blocages CORS).
			 * Peuple `this.results` avec les établissements retournés.
			 */
			async searchTraces() {
				if (!this.country_id || !this.selected_type) return

				const iso2 = this.getIso2(this.country_id)
				if (!iso2) {
					this.failureToast('tiers.address.trace_pays_invalide')
					return
				}

				const type_def = TRACE_TYPES.find(t => t.value === this.selected_type.value)
				if (!type_def) return

				this.loading = true
				this.searched = false
				this.results = []

				try {
					const data = await this.searchTracesEstablishments(iso2, type_def.apiCode)
					const list = Array.isArray(data) ? data : []
					this.results_truncated = list.length > MAX_RESULTS
					this.results = this.results_truncated ? list.slice(0, MAX_RESULTS) : list
				} catch (e) {
					console.error('AddressTraceImport::searchTraces => ERROR', e)
					this.failureToast('tiers.address.trace_erreur_api')
				} finally {
					this.loading = false
					this.searched = true
				}
			},
			/**
			 * Parse le champ street multilignes de l'API TRACES pour en extraire
			 * address1, address2, code postal et ville de manière structurée.
			 *
			 * @param {Object} item Établissement retourné par l'API TRACES.
			 * @returns {{ address1: string, address2: string, postalcode: string, town: string }}
			 */
			parseTraceAddress(item) {
				const cityRef = (item.address && item.address.cityReference) ? item.address.cityReference : null
				const streetRaw = (item.address && item.address.street && item.address.street.value) ? item.address.street.value : ''

				let postalcode = (cityRef && cityRef.postalCode) ? cityRef.postalCode : ''
				const town = (cityRef && cityRef.name) ? cityRef.name : ''

				// Eircode irlandais : ex. W91XA99, D01AB23
				const eircodeRegex = /^[A-Z]\d{2}\s?[A-Z0-9]{4}$/i
				// Ligne de comté : "Co. Kildare", "Co Kildare", "County Kildare"
				const countyRegex = /^(co\.?\s+|county\s+)/i

				const lines = streetRaw
					.split('\n')
					.map(l => l.replace(/,\s*$/, '').trim())
					.filter(Boolean)

				const addressLines = lines.filter(line => {
					// Exclure la ligne identique au nom de l'opérateur
					if (line.toLowerCase() === (item.operatorName || '').toLowerCase()) return false
					// Extraire et exclure l'Eircode
					if (eircodeRegex.test(line)) {
						if (!postalcode) postalcode = line
						return false
					}
					// Exclure les lignes de comté (capturées via town/region)
					if (countyRegex.test(line)) return false
					return true
				})

				return {
					address1: addressLines[0] || '',
					address2: addressLines.slice(1).join(', '),
					postalcode,
					town
				}
			},
			/**
			 * Formate l'adresse d'un établissement TRACES pour l'affichage dans la liste.
			 *
			 * @param {Object} item
			 * @returns {string}
			 */
			formatAddress(item) {
				const { address1, address2, postalcode, town } = this.parseTraceAddress(item)
				return [address1, address2, postalcode, town].filter(Boolean).join(', ')
			},
			/**
			 * Importe un établissement TRACES comme adresse de livraison du tiers.
			 * Si des champs obligatoires sont manquants (code postal, ville),
			 * ouvre le formulaire d'adresse pré-rempli plutôt que de sauvegarder directement.
			 *
			 * @param {Object} item Établissement retourné par l'API TRACES.
			 */
			async importAddress(item) {
				if (this.importing) return

				const { address1, address2, postalcode, town } = this.parseTraceAddress(item)

				const prefill = {
					tiersaddress_id: null,
					tiersaddress_label: item.operatorName || item.approvalNumber,
					tiersaddress_address1: address1,
					tiersaddress_address2: address2,
					tiersaddress_postalcode: postalcode,
					tiersaddress_town: town,
					tiersaddress_country: this.country_id,
					tiersaddress_region: '',
					tiersaddress_trace: item.approvalNumber || ''
				}

				if (!postalcode || !town) {
					this.$refs.modal.hide()
					this.$emit('open-form', prefill)
					return
				}

				this.importing = item.operatorId

				const result = await this.saveTiersAddress(parseInt(this.tiers_id), prefill, null)

				if (result) {
					this.successToast('tiers.address.trace_importe')
					this.$refs.modal.hide()
					this.$emit('submit')
				} else {
					this.failureToast('toast.info_modif_failed')
				}

				this.importing = null
			}
		},
		components: {
			CountryInput: () => import('GroomyRoot/components/Inputs/CountryInput')
		}
	}
</script>
